import type {Principal} from '../../../types/auth';
import type {ServerWebExchange} from '../../../types/web/server';

export type Authentication = Principal & {
    type: string;
    readonly name?: string;
    authenticated: boolean;
}

export function isAuthentication(principal?: Principal): principal is Authentication {
    return principal !== undefined && typeof principal['type'] === 'string' && typeof principal['authenticated'] === 'boolean';
}

export type AuthenticationConverter = (exchange: ServerWebExchange) => Promise<Authentication | undefined>;

export class AuthenticationError extends Error {
    private _authentication?: Authentication;

    get authentication(): Authentication | undefined {
        return this._authentication;
    }

    set authentication(value: Authentication) {
        if (value === undefined) {
            throw new TypeError("Authentication cannot be undefined");
        }
        this._authentication = value;
    }
}

export class InsufficientAuthenticationError extends AuthenticationError {}

export class BadCredentialsError extends AuthenticationError {}

export class AuthenticationCredentialsNotFoundError extends AuthenticationError {}

export class AccessDeniedError extends Error {}

export type AuthenticationManager = (authentication: Authentication) => Promise<Authentication | undefined>;

export type AuthorizationManager<T> = {

    /**
     * Determines if access should be granted to the given object for the provided authentication.
     * @param authentication
     * @param object
     */
    verify(authentication: Promise<Authentication | undefined>, object: T): Promise<void>,
    /**
     * Checks if the authentication is authorized to access the given object.
     * @param authentication
     * @param object
     */
    authorize: (authentication: Promise<Authentication | undefined>, object: T) => Promise<AuthorizationResult | undefined>;
}
export interface AuthorizationResult {readonly granted: boolean;}
export class AuthorizationDecision implements AuthorizationResult {
    constructor(granted: boolean) {
        this.granted = granted;
    }

    readonly granted: boolean;
}
export class DefaultAuthorizationManager<T> implements AuthorizationManager<T> {
    readonly #check: (authentication: Promise<Authentication | undefined>, object: T) => Promise<AuthorizationDecision | undefined>;

    constructor(check: (authentication: Promise<Authentication | undefined>, object: T) => Promise<AuthorizationDecision>) {
        this.#check = check;
    }

    async verify(authentication: Promise<Authentication | undefined>, object: T): Promise<void> {
        const decision = await this.#check(authentication, object);
        if (!decision?.granted) {
            throw new AccessDeniedError("Access denied");
        }
    }

    async authorize(authentication: Promise<Authentication | undefined>, object: T) {
        return await this.#check(authentication, object);
    }
}

export class AuthenticationServiceError extends AuthenticationError {}
export type ServerAuthenticationSuccessHandler = (filterExchange: {exchange: ServerWebExchange, next: () => Promise<void>}, authentication: Authentication) => Promise<void>;
export type ServerAuthenticationFailureHandler = (filterExchange: {exchange: ServerWebExchange, next: () => Promise<void>}, error: AuthenticationError) => Promise<void>;
export type ServerAuthenticationEntryPoint = (exchange: ServerWebExchange, error: AuthenticationError) => Promise<void>;
export type ServerAccessDeniedHandler = (exchange: ServerWebExchange, error: AccessDeniedError) => Promise<void>;
